"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, ArrowRight, ShieldCheck, FileSpreadsheet, Users as Users2, Scale } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const steps = [{
  number: "01",
  title: "Diagnóstico integral normativo y presupuestal",
  subtitle: "Evaluación preliminar de cumplimiento y tesorería",
  description: "Revisamos a fondo el estado de la cuenta pública, la normativa interna y las observaciones de ejercicios anteriores para trazar una ruta de solventación viable.",
  deliverables: "Matriz de riesgos, cronograma de regularización y dictamen normativo inicial.",
  icon: ShieldCheck
}, {
  number: "02",
  title: "Implementación de procedimientos y adecuación contable-administrativa",
  subtitle: "Alineación de procesos con criterios CONAC",
  description: "Configuramos catálogos de cuentas, matrices de conversión y manuales de procedimientos presupuestales para garantizar un registro patrimonial fidedigno.",
  deliverables: "Plan de cuentas armonizado, guías contabilizadoras y reportes financieros automáticos.",
  icon: FileSpreadsheet
}, {
  number: "03",
  title: "Capacitación continua al personal de las dependencias",
  subtitle: "Formación práctica para funcionarios y enlaces técnicos",
  description: "Instruimos a tesoreros, contadores y auditores internos en la operación diaria, evitando retrasos e inconsistencias técnicas al momento del cierre.",
  deliverables: "Talleres presenciales y remotos, manuales de usuario y cédulas de trabajo estandarizadas.",
  icon: Users2
}, {
  number: "04",
  title: "Seguimiento, auditoría preventiva y cierre de cuenta pública",
  subtitle: "Acompañamiento en fiscalización y entrega de resultados",
  description: "Efectuamos revisiones previas a los plazos legales de entrega para solventar discrepancias antes de la fiscalización superior del estado o federación.",
  deliverables: "Expediente de cuenta pública integrado, cédulas de auditoría y soporte documental listo.",
  icon: Scale
}];
export default function Methodology() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="cómo-trabajamos-con-entes-públicos" data-nav="light" className="w-full bg-background py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        {/* Section Header */}
        <div className="max-w-3xl mb-14 lg:mb-20">
          <Badge variant="outline" className="mb-4 text-xs font-serif font-bold uppercase tracking-widest border-border bg-card text-foreground px-3 py-1 rounded-sm">
            Metodología Institucional
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-wide text-foreground uppercase leading-tight">
            Cómo trabajamos con entes públicos
          </h2>
          <div className="mt-4 mb-5 h-[2px] w-12 bg-primary" />
          <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            Un proceso de cuatro etapas diseñado para dotar de certeza técnica a tesorerías municipales, 
            organismos descentralizados y dependencias estatales, asegurando apego cabal al marco CONAC y de disciplina financiera.
          </p>
        </div>

        {/* 2-Column Institutional Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Left Column: Contextual Working Image & Briefing Dossier */}
          <div className="lg:col-span-5 flex flex-col gap-6 lg:sticky lg:top-24">
            <div className="relative overflow-hidden rounded-md border border-border shadow-md bg-card">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/government_consulting_session.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/government_consulting_session.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/government_consulting_session.png-wvc-sizes" alt="Sesión técnica de trabajo institucional y análisis presupuestal con funcionarios públicos" loading="lazy" decoding="async" className="w-full h-80 sm:h-96 object-cover object-center" />
              <div className="p-6 bg-card border-t border-border flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-primary" />
                  <span className="text-xs font-serif uppercase tracking-widest text-muted-foreground font-semibold">
                    Supervisión Senior Directa
                  </span>
                </div>
                <h3 className="text-base sm:text-lg font-serif font-semibold text-foreground tracking-normal uppercase">
                  Acompañamiento en mesas técnicas
                </h3>
                <p className="text-sm text-muted-foreground font-sans leading-normal">
                  Cada fase es coordinada por especialistas con experiencia directa en órganos de control, auditoría superior y hacienda municipal.
                </p>
                <div className="pt-2">
                  <Button asChild variant="outline" className="w-full justify-between font-serif uppercase tracking-wider text-xs border-border">
                    <Link to="/ContactPage">
                      <span>Solicitar diagnóstico municipal</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>

            {/* Credibility micro-strip */}
            <div className="rounded-md border border-border bg-card p-5 shadow-2xs">
              <div className="flex items-start gap-4">
                <div className="p-2.5 rounded-sm bg-muted text-primary shrink-0">
                  <ShieldCheck className="size-5" />
                </div>
                <div className="flex flex-col gap-1">
                  <h4 className="text-sm font-serif font-semibold text-foreground uppercase tracking-wider">
                    Cumplimiento y transparencia
                  </h4>
                  <p className="text-xs text-muted-foreground font-sans leading-relaxed">
                    Estrategias diseñadas para prevenir responsabilidades administrativas y superar auditorías de fiscalización sin salvedades.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: 4-Step Vertical Process Track */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }} className="lg:col-span-7 flex flex-col gap-5">
            {steps.map((step, i) => {
            const IconComponent = step.icon;
            return <motion.div key={step.number} data-index={i} variants={itemVariants} className="rounded-md border border-border bg-card p-6 sm:p-8 shadow-xs hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                  <div className="flex items-start gap-4 sm:gap-6">
                    {/* Step Numeral Pill */}
                    <div className="flex flex-col items-center shrink-0">
                      <div className="flex size-11 sm:size-12 items-center justify-center rounded-sm border border-primary/20 bg-muted font-serif text-base sm:text-lg font-bold text-foreground">
                        {step.number}
                      </div>
                      {i < steps.length - 1 && <div className="w-px h-8 bg-border hidden sm:block mt-3" />}
                    </div>

                    {/* Step Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="text-xs font-serif uppercase tracking-widest text-muted-foreground font-semibold">
                          Fase operativa
                        </span>
                        <span className="text-muted-foreground text-xs">{"·"}</span>
                        <span className="text-xs font-sans text-muted-foreground">
                          {step.subtitle}
                        </span>
                      </div>

                      <h3 className="text-lg sm:text-xl font-serif font-semibold text-foreground uppercase tracking-wide leading-snug">
                        {step.title}
                      </h3>

                      <p className="mt-3 text-sm sm:text-base text-muted-foreground font-sans leading-relaxed">
                        {step.description}
                      </p>

                      <Separator className="my-4 bg-border/60" />

                      {/* Deliverable info */}
                      <div className="flex items-start gap-2.5 pt-1">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <div className="text-xs sm:text-sm font-sans text-foreground">
                          <span className="font-semibold text-foreground">Entregables: </span>
                          <span className="text-muted-foreground">{step.deliverables}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>;
          })}

            {/* Bottom Call to Action strip */}
            <div className="mt-4 p-6 sm:p-8 rounded-md border border-border bg-muted/50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div className="space-y-1">
                <h4 className="text-base font-serif font-semibold text-foreground uppercase tracking-wide">
                  ¿Inicia periodo de entrega-recepción o cierre fiscal?
                </h4>
                <p className="text-sm text-muted-foreground font-sans">
                  Agende una reunión de encuadre con nuestro equipo de consultores gubernamentales.
                </p>
              </div>
              <Button asChild size="lg" className="shrink-0 font-serif uppercase tracking-wider text-xs">
                <Link to="/ContactPage">
                  Hablar con un especialista
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}